/**
 * useBlockedUsers Hook
 * Fetches paginated blocked users list for settings.
 * Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { BlockedUser, PaginatedBlockList } from '../blockTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseBlockedUsersOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
  pageSize?: number;
}

export interface UseBlockedUsersResult {
  users: BlockedUser[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  fetched: boolean;
  error: string | null;
  loadBlockedUsers: () => Promise<void>;
  loadMore: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useBlockedUsers(options: UseBlockedUsersOptions): UseBlockedUsersResult {
  const { httpClient, isAuthenticated, pageSize = 20 } = options;

  const [users, setUsers] = useState<BlockedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBlockedUsers = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedBlockList>('/api/blocks', {
        limit: pageSize,
        offset: 0,
      });
      setUsers(data.users);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blocked users');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [httpClient, isAuthenticated, pageSize]);

  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !hasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedBlockList>('/api/blocks', {
        limit: pageSize,
        offset: users.length,
      });
      setUsers((prev) => [...prev, ...data.users]);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more blocked users');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, hasMore, loading, users.length, pageSize]);

  return {
    users,
    total,
    hasMore,
    loading,
    fetched,
    error,
    loadBlockedUsers,
    loadMore,
  };
}
